import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Answer } from '@/types/workerInfo.types';
import { colors, spacing } from '@/theme';
import { Calendar, Check } from 'lucide-react';

interface DateInputProps {
  answer: Answer;
  onBlur: (value: { dateValue: string }) => void;
  disabled?: boolean;
}

export function DateInput({ answer, onBlur, disabled }: DateInputProps) {
  const [value, setValue] = useState(answer.dateValue || '');
  const [isFocused, setIsFocused] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const calendarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setValue(answer.dateValue || '');
  }, [answer.dateValue]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target as Node)) {
        setShowCalendar(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleBlur = () => {
    setIsFocused(false);
    setShowCalendar(false);
    
    if (value !== answer.dateValue) {
      onBlur({ dateValue: value });
      setIsSaved(true);
      setTimeout(() => setIsSaved(false), 2000);
    }
  };

  const handleToday = () => {
    const today = new Date().toISOString().split('T')[0];
    setValue(today);
    onBlur({ dateValue: today });
    setShowCalendar(false);
    setIsSaved(true);
    setTimeout(() => setIsSaved(false), 2000);
  };

  return (
    <div style={{ position: 'relative' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: spacing[2],
        }}
      >
        <div>
          <label
            style={{
              fontSize: 16,
              fontWeight: 600,
              color: colors.gray[900],
              display: 'flex',
              alignItems: 'center',
              gap: spacing[2],
              marginBottom: spacing[1],
            }}
          >
            <Calendar size={16} color={colors.primary[500]} />
            {answer.question.question}
            {answer.question.isRequired && (
              <span style={{ color: colors.error?.[500] || '#ef4444', marginLeft: 4 }}>*</span>
            )}
          </label>
          {answer.question.description && (
            <p style={{ fontSize: 14, color: colors.gray[600], marginBottom: spacing[2] }}>
              {answer.question.description}
            </p>
          )}
        </div>

        <AnimatePresence>
          {isSaved && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: spacing[1],
                color: colors.success?.[500] || '#10b981',
                fontSize: 14,
                fontWeight: 500,
              }}
            >
              <Check size={16} />
              <span>Guardado</span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div style={{ position: 'relative' }}>
        <input
          ref={inputRef}
          type="date"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onFocus={() => {
            setIsFocused(true);
            setShowCalendar(true);
          }}
          onBlur={handleBlur}
          disabled={disabled}
          style={{
            width: '100%',
            padding: spacing[4],
            paddingRight: spacing[8],
            borderRadius: 16,
            border: `2px solid ${
              isFocused ? colors.primary[500] : colors.gray[200]
            }`,
            fontSize: 15,
            fontFamily: 'inherit',
            outline: 'none',
            transition: 'all 0.2s ease',
            backgroundColor: isFocused ? colors.white : colors.gray[50],
            color: colors.gray[900],
            cursor: 'pointer',
          }}
        />
        
        <div
          style={{
            position: 'absolute',
            right: spacing[3],
            top: '50%',
            transform: 'translateY(-50%)',
            color: isFocused ? colors.primary[500] : colors.gray[400],
            pointerEvents: 'none',
          }}
        >
          <Calendar size={18} />
        </div>
      </div>

      <AnimatePresence>
        {showCalendar && (
          <motion.div
            ref={calendarRef}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              marginTop: spacing[2],
              padding: spacing[3],
              backgroundColor: colors.white,
              borderRadius: 12,
              border: `1px solid ${colors.gray[200]}`,
              boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
              zIndex: 10,
            }}
          >
            <button
              onClick={handleToday}
              style={{
                width: '100%',
                padding: spacing[2],
                backgroundColor: colors.primary[50],
                border: `1px solid ${colors.primary[200]}`,
                borderRadius: 8,
                color: colors.primary[700],
                fontSize: 14,
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = colors.primary[100];
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = colors.primary[50];
              }}
            >
              Seleccionar hoy
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}